/**
 * Schedule Entity Schemas
 *
 * Types for schedules, calendar events, and meetings.
 */

import { z } from 'zod'

/**
 * Schedule Block Schema
 */
export const ScheduleBlockSchema = z.object({
  id: z.string(),
  name: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  dayOfWeek: z.number().optional(),
  date: z.string().optional(),
  location: z.string().nullable().optional(),
  teacher: z.string().nullable().optional(),
  courseId: z.string().nullable().optional(),
  sectionId: z.string().nullable().optional(),
  color: z.string().nullable().optional()
})

export type ScheduleBlock = z.infer<typeof ScheduleBlockSchema>

/**
 * Day Schedule Schema
 */
export const DayScheduleSchema = z.object({
  date: z.string(),
  dayName: z.string().optional(),
  blocks: z.array(ScheduleBlockSchema)
})

export type DaySchedule = z.infer<typeof DayScheduleSchema>

/**
 * User Schedule Schema
 */
export const UserScheduleSchema = z.object({
  userId: z.string(),
  schedules: z.array(DayScheduleSchema),
  timezone: z.string().optional()
})

export type UserSchedule = z.infer<typeof UserScheduleSchema>

/**
 * Meeting Type Schema
 */
export const MeetingTypeSchema = z.enum(['CLASS', 'OFFICE_HOURS', 'MEETING', 'EVENT'])
export type MeetingType = z.infer<typeof MeetingTypeSchema>

/**
 * Meeting Schema
 */
export const MeetingSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: MeetingTypeSchema,
  startTime: z.string(),
  endTime: z.string(),
  location: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  organizer: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .optional(),
  attendees: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        status: z.string().optional()
      })
    )
    .optional(),
  isRecurring: z.boolean().optional(),
  meetingUrl: z.string().nullable().optional()
})

export type Meeting = z.infer<typeof MeetingSchema>
